import { useState } from "react";
import {
    Box,
    Grid,
    Paper,
    Typography,
    Chip,
    Button,
    Table,
    TableHead,
    TableRow,
    TableCell,
    TableBody,
    IconButton,
    Divider,
    Stack,
    Alert,
    AlertTitle
} from "@mui/material";
import VisibilityIcon from "@mui/icons-material/Visibility";
import { useQuery } from "react-query";
import DialogFullScreen from "../../../../../../components/DialogFullScreen";
import LoadingApiQuery from "../../../../../../components/LoadingApiQuery";
import axiosClient from "../../../../../../lib/axios";
import { DetalheOCData, ProdutoOC } from "../../../../../../types/api/estoque/detalhar-ordem-compra";
import PrintIcon from '@mui/icons-material/Print';
import useImprimirArquivo from "../../../../../../lib/useImprimirArquivo";
import { ConfirmProvider } from "material-ui-confirm";
import moment from "moment";
import DialogHistoricoProduto from "./DialogHistoricoProduto";
import DialogCancelarOC from "./DialogCancelarOC";

export default function DialogDetalheOC({ open, value, handleClose }: DialogDetalheOCProps) {

    const imprimirArquivo = useImprimirArquivo()

    const { data, isLoading } = useQuery<DetalheOCData>({
        queryKey: ["detalhe-ordem-compra", value, open],
        queryFn: async () => {
            if (!open) return undefined as any;
            const res = await axiosClient.get(`/estoque/compras/detalhar-ordem-compra/${value}`);
            return res.data;
        },
        enabled: !!open && !!value,
        cacheTime: 0,
    });

    const [openHistorico, setOpenHistorico] = useState(false);
    const [produtoSelecionado, setProdutoSelecionado] = useState<ProdutoOC | null>(null);
    const [openCancelarOC, setOpenCancelarOC] = useState(false);

    const handleOpenHistorico = (produto: ProdutoOC) => {
        setProdutoSelecionado(produto);
        setOpenHistorico(true);
    };

    const handleCloseHistorico = () => {
        setOpenHistorico(false);
        setProdutoSelecionado(null);
    };

    function formatarValorMonetario(valor: number | string): string {
        let num = Number(valor);
        return `R$ ${num.toLocaleString("pt-BR", { minimumFractionDigits: 2 })}`;
    }

    function formatarQuantidade(quantidade: number | string, aceitaDecimal: boolean): string {
        if (!aceitaDecimal) {
            const num = Number(quantidade);
            return Math.round(num).toString();
        }

        return quantidade.toString().replace('.', ',');
    }

    const dialogTitle = data?.oc_cancelada
        ? `Detalhes da OC - CANCELADA`
        : "Detalhes da OC";

    const handleImprimir = (numeroOc: string) => {
        imprimirArquivo("estoque/compras/imprimir-comprovante-compra/" + numeroOc + '?segunda_via=true')
    }

    return (
        <DialogFullScreen open={open} handleClose={handleClose} title={dialogTitle}>
            {isLoading && <LoadingApiQuery />}
            {!isLoading && data && (
                <Box>
                    <Paper sx={{ p: 3, mb: 3, borderRadius: 2 }}>
                        {data.oc_cancelada && (
                            <Alert severity="error" sx={{ mb: 3 }}>
                                <AlertTitle sx={{ fontWeight: "bold" }}>
                                    Ordem de Compra Cancelada
                                </AlertTitle>
                                <Typography variant="body2" sx={{ mb: 1 }}>
                                    <strong>Data do cancelamento:</strong> {data.data_cancelamento ? moment(data.data_cancelamento).format('DD/MM/YYYY') : "N/A"}
                                </Typography>
                                <Typography variant="body2">
                                    <strong>Motivo:</strong> {data.motivo_cancelamento || "Não informado"}
                                </Typography>
                            </Alert>
                        )}

                        <Box sx={{ display: "flex", flexDirection: "row", justifyContent: "space-between", alignItems: "flex-start" }}>
                            <Box sx={{ flex: "1", pr: 2 }}>
                                <Stack direction={'row'} alignItems={"center"} justifyContent={"space-between"}>
                                    <Typography variant="h6" sx={{ mb: 2, fontWeight: "bold" }}>
                                        Ordem de compra
                                    </Typography>
                                    <Button
                                        variant="outlined"
                                        color="primary"
                                        onClick={() => { handleImprimir(data.numero_ordem_compra) }}
                                        endIcon={<PrintIcon />}
                                        disabled={isLoading || data.oc_cancelada}
                                    >
                                        Imprimir
                                    </Button>
                                </Stack>

                                <Grid container spacing={0.5}>
                                    <Grid size={{ xs: 12, sm: 6, md: 6 }}>
                                        <Typography variant="body2" sx={{ fontWeight: "bold" }}>Número da ordem</Typography>
                                        <Typography variant="body2" sx={{ mb: 1 }}>
                                            <Button
                                                variant="text"
                                                sx={{ color: "#2756B2", textDecoration: "underline", fontWeight: "bold", fontSize: "1rem", p: 0, minWidth: "unset" }}
                                            >
                                                {data.numero_ordem_compra}
                                            </Button>
                                        </Typography>
                                    </Grid>
                                    <Grid size={{ xs: 12, sm: 6, md: 6 }}>
                                        <Typography variant="body2" sx={{ fontWeight: "bold" }}>Fornecedor</Typography>
                                        <Typography variant="body2" sx={{ mb: 1 }}>
                                            {data.fornecedor ?? "-"}
                                        </Typography>
                                    </Grid>
                                    <Grid size={{ xs: 12, sm: 6, md: 6 }}>
                                        <Typography variant="body2" sx={{ fontWeight: "bold" }}>Comprador</Typography>
                                        <Typography variant="body2" sx={{ mb: 1 }}>
                                            {data.destino ?? "-"}
                                        </Typography>
                                    </Grid>
                                    <Grid size={{ xs: 12, sm: 6, md: 6 }}>
                                        <Typography variant="body2" sx={{ fontWeight: "bold" }}>Solicitante</Typography>
                                        <Typography variant="body2" sx={{ mb: 1 }}>
                                            {data.solicitante ?? "-"}
                                        </Typography>
                                    </Grid>
                                    <Grid size={{ xs: 12, sm: 6, md: 6 }}>
                                        <Typography variant="body2" sx={{ fontWeight: "bold" }}>Solicitada em</Typography>
                                        <Typography variant="body2" sx={{ mb: 1 }}>
                                            {data.data_solicitacao ?? "-"}
                                        </Typography>
                                    </Grid>
                                    <Grid size={{ xs: 12, sm: 6, md: 6 }}>
                                        <Typography variant="body2" sx={{ fontWeight: "bold" }}>Contato</Typography>
                                        <Typography variant="body2" sx={{ mb: 1 }}>
                                            {data.cotacao?.vendedor.nome_vendedor ?? "-"}
                                        </Typography>
                                    </Grid>
                                </Grid>
                            </Box>

                        </Box>
                        <Divider sx={{ mt: 2, mb: 2 }} />

                        <Box sx={{ mt: 3 }}>
                            <Typography variant="h6" sx={{ mb: 2, fontWeight: "bold" }}>
                                Informações de Pagamento
                            </Typography>

                            {data.parcelas && data.parcelas.length > 0 && (
                                <Box sx={{ mb: 3 }}>
                                    <Typography variant="subtitle1" sx={{ fontWeight: "bold", mb: 1 }}>
                                        Parcelas ({data.parcelas.length})
                                    </Typography>

                                    <Box sx={{
                                        maxHeight: 200,
                                        overflowY: 'auto',
                                        border: '1px solid #e0e0e0',
                                        borderRadius: 1,
                                        p: 1
                                    }}>
                                        <Grid container spacing={1}>
                                            {data.parcelas.map((parcela, index) => (
                                                <Grid size={{ xs: 12, sm: 6, md: 4, lg: 3 }} key={index}>
                                                    <Paper sx={{
                                                        p: 1.5,
                                                        border: "1px solid #e0e0e0",
                                                        height: '100%',
                                                        minHeight: 80
                                                    }}>
                                                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                                                            <Typography variant="body2" sx={{ fontWeight: "bold", color: "#666" }}>
                                                                {index + 1}ª Parcela
                                                            </Typography>
                                                            <Typography variant="body2" sx={{
                                                                fontWeight: "bold",
                                                                color: "#2756B2",
                                                                fontSize: '0.9rem'
                                                            }}>
                                                                {formatarValorMonetario(parcela.valor)}
                                                            </Typography>
                                                        </Box>
                                                        <Typography variant="body2" sx={{
                                                            color: "#888",
                                                            mt: 0.5,
                                                            fontSize: '0.8rem'
                                                        }}>
                                                            Vencimento: {data.forma_primeira_parcela === "personalizada" ? new Date(parcela.data_vencimento).toLocaleDateString('pt-BR') : parcela.data_vencimento}
                                                        </Typography>
                                                    </Paper>
                                                </Grid>
                                            ))}
                                        </Grid>
                                    </Box>
                                </Box>
                            )}

                            <Box
                                sx={{
                                    display: "flex",
                                    justifyContent: "space-between",
                                    alignItems: "center",
                                    p: 2,
                                    bgcolor: "#f5f5f5",
                                    borderRadius: 1
                                }}
                            >
                                <Box>
                                    <Typography variant="body2" sx={{ color: "#666" }}>
                                        Valor produtos
                                    </Typography>
                                    <Typography variant="h6" sx={{ fontWeight: "bold" }}>
                                        {formatarValorMonetario(data.valor_subtotal ?? 0)}
                                    </Typography>
                                </Box>

                                <Box sx={{ textAlign: "center" }}>
                                    <Typography variant="body2" sx={{ color: "#666" }}>
                                        Descontos
                                    </Typography>
                                    <Typography variant="h6" sx={{ fontWeight: "bold" }}>
                                        {formatarValorMonetario(data.valor_desconto ?? 0)}
                                    </Typography>
                                </Box>

                                <Box sx={{ textAlign: "center" }}>
                                    <Typography variant="body2" sx={{ color: "#666" }}>
                                        Acréscimos
                                    </Typography>
                                    <Typography variant="h6" sx={{ fontWeight: "bold" }}>
                                        {formatarValorMonetario(data.valor_acrescimo ?? 0)}
                                    </Typography>
                                </Box>

                                <Box sx={{ textAlign: "center" }}>
                                    <Typography variant="body2" sx={{ color: "#666" }}>
                                        Frete
                                    </Typography>
                                    <Typography variant="h6" sx={{ fontWeight: "bold" }}>
                                        {formatarValorMonetario(data.valor_frete ?? 0)}
                                    </Typography>
                                </Box>

                                <Box sx={{ textAlign: "right" }}>
                                    <Typography variant="body2" sx={{ color: "#666" }}>
                                        Total Geral
                                    </Typography>
                                    <Typography variant="h5" sx={{ fontWeight: "bold", color: "#2756B2" }}>
                                        {formatarValorMonetario(data.valor_total ?? 0)}
                                    </Typography>
                                </Box>
                            </Box>
                        </Box>
                    </Paper>

                    <Paper sx={{ p: 3, borderRadius: 2, mb: 3 }}>
                        <Typography variant="h6" sx={{ mb: 2 }}>
                            Produtos da OC
                        </Typography>
                        <Table>
                            <TableHead>
                                <TableRow>
                                    <TableCell sx={{ fontWeight: "bold" }}>Produto</TableCell>
                                    <TableCell align="right" sx={{ fontWeight: "bold" }}>Qtd.</TableCell>
                                    <TableCell align="right" sx={{ fontWeight: "bold" }}>Valor Unit.</TableCell>
                                    <TableCell align="right" sx={{ fontWeight: "bold" }}>Desconto Unit.</TableCell>
                                    <TableCell align="right" sx={{ fontWeight: "bold" }}>Acréscimo Unit.</TableCell>
                                    <TableCell align="right" sx={{ fontWeight: "bold" }}>Valor Total</TableCell>
                                    <TableCell align="right" sx={{ fontWeight: "bold" }}>Qtd. entregue</TableCell>
                                    <TableCell align="left" sx={{ fontWeight: "bold" }}>Status</TableCell>
                                    <TableCell sx={{ fontWeight: "bold" }} align="left">Ações</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {(data.itens ?? []).map((produto) => (
                                    <TableRow key={produto.id}>
                                        <TableCell>
                                            <Typography variant="body2" fontWeight="bold" sx={{ color: "#2756B2", textDecoration: "underline", cursor: "pointer" }}>
                                                {produto.produto_id}
                                            </Typography>
                                            <Typography variant="body2" sx={{ color: "#888", fontSize: "0.9rem", mt: 0.5 }}>
                                                {produto.nome}
                                            </Typography>
                                            <Typography variant="body2" sx={{ color: "#888", fontSize: "0.9rem" }}>
                                                {produto.descricao}
                                            </Typography>
                                        </TableCell>
                                        <TableCell align="right">{formatarQuantidade(produto.quantidade, produto.aceita_decimal)} {produto.unidade_medida}</TableCell>
                                        <TableCell align="right">{formatarValorMonetario(produto.valor_unitario ?? 0)}</TableCell>
                                        <TableCell align="right">{formatarValorMonetario(produto.valor_desconto_unitario ?? 0)}</TableCell>
                                        <TableCell align="right">{formatarValorMonetario(produto.valor_acrescimo_unitario ?? 0)}</TableCell>
                                        <TableCell align="right">{formatarValorMonetario(produto.valor_total ?? 0)}</TableCell>
                                        <TableCell align="right">{produto.quantidade_entregue ?? 0}</TableCell>
                                        <TableCell>
                                            {produto.status ? <Chip {...getStatusChipProps(produto.status)} /> : null}
                                        </TableCell>
                                        <TableCell align="left">
                                            <Box sx={{ display: "flex", gap: 1 }}>
                                                <IconButton size="small" onClick={() => handleOpenHistorico(produto)}>
                                                    <VisibilityIcon />
                                                </IconButton>
                                            </Box>
                                        </TableCell>
                                    </TableRow>
                                ))}
                                {(!data.itens || data.itens.length === 0) && (
                                    <TableRow>
                                        <TableCell colSpan={7} align="center">
                                            Nenhum produto encontrado.
                                        </TableCell>
                                    </TableRow>
                                )}
                            </TableBody>
                        </Table>

                        {!data.oc_cancelada && (
                            <Stack direction="row" justifyContent="flex-end" mt={2}>
                                <Button variant="contained" color="error" onClick={() => setOpenCancelarOC(true)}>
                                    Cancelar ordem de compra
                                </Button>
                            </Stack>
                        )}
                    </Paper>
                </Box>
            )}

            {produtoSelecionado && (
                <DialogHistoricoProduto
                    open={openHistorico}
                    onClose={handleCloseHistorico}
                    numeroOC={data?.numero_ordem_compra ?? ""}
                    produto={produtoSelecionado.nome ?? ""}
                    valorTotal={Number(produtoSelecionado.valor_total ?? 0)}
                    entregue={Number(produtoSelecionado.quantidade_entregue ?? 0)}
                    quantidade={Number(produtoSelecionado.quantidade ?? 0)}
                    eventos={produtoSelecionado.historico ?? []}
                />
            )}

            {
                openCancelarOC && (
                    <ConfirmProvider defaultOptions={{
                        title: 'Confirmar cancelamento da OC',
                        cancellationText: "Não",
                        confirmationText: "Sim",
                        confirmationButtonProps: { variant: 'contained', color: 'error' },
                        cancellationButtonProps: { variant: 'outlined', color: 'error' },
                    }}>
                        <DialogCancelarOC
                            open={openCancelarOC}
                            solicitacaoCodigo={data?.solicitacao_codigo ?? ""}
                            onClose={() => setOpenCancelarOC(false)}
                            numeroOC={data?.numero_ordem_compra ?? ""}
                        />
                    </ConfirmProvider>
                )
            }
        </DialogFullScreen>
    );
}

function getStatusChipProps(status: string) {
    let background = "#E0E0E0";
    let textColor = "#444";
    let muiColor: "default" | "error" | "primary" | "secondary" | "success" | "info" | "warning" = "default";
    if (status === "Concluída" || status === "Recebido") {
        background = "#2E7D32";
        textColor = "#fff";
        muiColor = "success";
    } else if (status === "OC Emitida" || status === "Parcial") {
        background = "#46B36F";
        textColor = "#fff";
        muiColor = "info";
    }
    return {
        sx: {
            fontWeight: "bold",
            fontSize: "0.8125rem",
            px: 2,
            height: 28,
            borderRadius: 5,
            background,
            color: textColor,
        },
        color: muiColor,
        label: status,
    };
}

interface DialogDetalheOCProps {
    open: boolean;
    value: string;
    handleClose: () => void;
}